import { useEffect, useState } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { keyToLabel, scoreToLabel, getBadgeColorRating } from './utils';
import { Rating } from '../../common/types/common';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface TopBenefit {
  title: string;
  label: string;
  tooltipContent?: string;
}

interface TopBenefitsProps {
  allTopBenefits?: TopBenefit[];
  value_driver_score?: Record<string, number>;
  isEditing?: boolean;
  solutionData?: any;
  setSolutionData?: (data: any) => void;
}

const TopBenefits: React.FC<TopBenefitsProps> = ({
  allTopBenefits,
  value_driver_score,
  isEditing,
  solutionData,
  setSolutionData,
}) => {
  const [benefits, setBenefits] = useState<TopBenefit[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    if (allTopBenefits) {
      const reordered = [
        ...allTopBenefits.filter((b) => b.title !== 'Cost Savings'),
        ...allTopBenefits.filter((b) => b.title === 'Cost Savings'),
      ];
      setBenefits(reordered);
    } else if (value_driver_score) {
      const newBenefits = Object.entries(value_driver_score).map(([key, score]) => {
        const numericScore = typeof score === 'number' ? score : 0;
        return {
          title: keyToLabel(key),
          label: scoreToLabel(numericScore),
        };
      });

      const reordered = [
        ...newBenefits.filter((b) => b.title !== 'Cost Savings'),
        ...newBenefits.filter((b) => b.title === 'Cost Savings'),
      ];
      setBenefits(reordered);
    }
  }, [allTopBenefits, value_driver_score]);

  const handleBenefitChange = (benefit: string, value: string) => {
    if (setSolutionData) {
      setSolutionData({
        ...solutionData,
        benefits: { ...solutionData.benefits, [benefit]: value },
      });
    }
  };

  if (benefits.length === 0) {
    return (
      <Box sx={{ borderRadius: 1 }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'common.white', p: 1.5, fontWeight: 'bold' }}>
          <Typography>Top Benefits</Typography>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: '#DDE1E6',
            borderTop: 'none',
            bgcolor: 'common.white',
            p: 2,
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary">No benefits data available</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ bgcolor: 'primary.main', color: 'common.white', p: 1.5, fontWeight: 'bold' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <Box component="img" src="/images/chart-line_L.svg" sx={{ mr: 1.5 }} />
          <Typography>Benefits</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          borderTop: 'none',
          bgcolor: 'common.white',
          py: 3,
          gap: 2,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        {benefits.map((benefit, index) => {
          const { bgColor } = getBadgeColorRating(benefit.label as Rating);
          return (
            <Box
              key={`${benefit.title}-${index}`}
              sx={{
                width: { xs: '90%', sm: '45%', md: '80%' },
                minHeight: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'grey.300',
                borderBottom: `3px solid ${bgColor}`,
                borderRadius: 2,
                bgcolor: 'white',
                boxShadow: 1,
                py: 1,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  mb: 0.5,
                }}
              >
                {benefit.title}
              </Typography>

              {isEditing ? (
                <Select
                  variant="standard"
                  sx={{
                    width: '100%',
                    textTransform: 'uppercase',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: 1,
                    bgcolor: 'white',
                    borderRadius: 1,
                    '& .MuiSelect-select': {
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      paddingRight: '16px !important',
                      paddingLeft: '16px !important',
                    },
                  }}
                  disableUnderline
                  value={benefit.label}
                  IconComponent={() => null}
                  open={openIndex === index}
                  onOpen={() => setOpenIndex(index)}
                  onClose={() => setOpenIndex(null)}
                  renderValue={(value) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <span>{value}</span>
                      {openIndex === index ? (
                        <KeyboardArrowUpIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: '1.5rem', color: 'text.secondary' }}
                        />
                      )}
                    </div>
                  )}
                  MenuProps={{
                    disablePortal: true,
                    sx: {
                      top: '16px',
                      '& .MuiPaper-root': {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: 1,
                      },
                      '& .MuiMenuItem-root': {
                        justifyContent: 'left',
                        fontSize: '1rem',
                        fontWeight: 600,
                      },
                    },
                  }}
                  onChange={(e) => handleBenefitChange(benefit.title, e.target.value)}
                >
                  <MenuItem value={'LOW'}>LOW</MenuItem>
                  <MenuItem value={'MEDIUM'}>MEDIUM</MenuItem>
                  <MenuItem value={'HIGH'}>HIGH</MenuItem>
                </Select>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '1.1rem',
                    letterSpacing: 1,
                  }}
                >
                  {benefit.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TopBenefits;
